import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { signInWithCustomToken } from 'firebase/auth';
import { auth, functions } from '../lib/firebase';
import { useAuth } from '../hooks/useAuth';
import { useBoards, fetchBoardLists } from '../hooks/useData';
import { useSharedListPlacements } from '../hooks/useSharedListPlacements';
import { TEMPLATE_BLANK, stripTemplateContent } from '../lib/boardTemplates';
import Wordmark from '../components/Wordmark';
import BoardPickerModal from '../components/BoardPickerModal';

/**
 * AcceptInvitePage — landing page for emailed share-invite links.
 *
 * URL: /accept-invite/:token
 *
 * Flow:
 *   1. User clicks the accept-invite link from their email.
 *   2. If not signed in, render a compact sign-in widget (Google /
 *      magic-link / guest is hidden — anonymous can't be tied to an
 *      email so it doesn't make sense for invites).
 *   3. Once user is signed in, automatically call the acceptShareInvite
 *      Cloud Function with the token.
 *   4. On success, the function returns { redirectTo: '/board/...' };
 *      we navigate there. The acceptor's sharedWithMe pointer is
 *      written server-side so the sidebar shows the new entry.
 *   5. On error (invalid / expired / already-claimed), show a clear
 *      message and a button back to the app root.
 *
 * The token is single-use: once accepted, it's stamped acceptedAt +
 * acceptedByUid so a re-visit won't double-add or be claimable by a
 * different user. Re-visits by the same accepting user are idempotent
 * — the Cloud Function returns the same redirectTo.
 *
 * Note on auth gating: anyone with a valid token can use it, even
 * if their email differs from the invited email. This matches the
 * spec — the invitee may want to sign up with a different email
 * than the one they received the invite at. The token is the
 * authorization, not the email match.
 */
export default function AcceptInvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const {
    user,
    signInWithGoogle,
    sendMagicLink,
  } = useAuth();

  // Status machine: 'awaiting-signin' | 'accepting' | 'picking' | 'success' | 'error'
  // 'awaiting-signin' shows the sign-in widget. As soon as user
  // becomes truthy, we transition to 'accepting' and fire the call.
  // 'picking' = list invites only: post-accept, before the redirect,
  // we show a BoardPickerModal so the invitee can pick where the
  // shared list will live in their own boards (Walter 2026-05-11
  // sharing-refinements).
  const [status, setStatus] = useState('awaiting-signin');
  const [errorMsg, setErrorMsg] = useState('');
  const [magicState, setMagicState] = useState('idle');
  const [magicEmail, setMagicEmail] = useState('');
  // Invite metadata fetched BEFORE sign-in via peekShareInvite. Used
  // to pre-populate the email field and show a friendly "Tom invited
  // you to 'main'" line. Null until the peek resolves; treat absence
  // as "fall back to generic copy".
  const [inviteInfo, setInviteInfo] = useState(null);
  // Cache of the acceptShareInvite response so the post-accept
  // board-picker step has the source-owner/board ids to write the
  // sharedListPlacements doc without a second resolve round-trip.
  // Shape: { kind, ownerUid, boardId, listId, targetTitle, redirectTo }.
  const [acceptResult, setAcceptResult] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Guard so we only fire acceptShareInvite once per mount even
  // through a sign-in → state-rerender flurry. Without this, React
  // strict-mode double-mount in dev double-fires the call (which the
  // server handles idempotently but produces a confusing extra log).
  const acceptCalledRef = useRef(false);
  // Same guard for the post-accept transition: useEffect can fire
  // twice on a single acceptResult change due to React's strict-mode
  // double-invoke; without this, we'd open the picker twice.
  const placementTransitionRef = useRef(false);

  // Subscribe to the user's own boards once they're signed in. Powers
  // the BoardPickerModal's dropdown for list-invites; idle (returns
  // []) until user is set.
  const { boards: userBoards, addBoardFromTemplate } = useBoards(user?.uid);
  // Placement writer. setPlacement(listId, { sourceOwnerUid,
  // sourceBoardId, boardId }) creates the per-user record that
  // BoardPage's merge layer reads.
  const { setPlacement } = useSharedListPlacements(user?.uid);

  // Peek at the invite metadata once on mount. Unauthenticated call,
  // returns only the safe-to-show fields. The bottom magic-link
  // email input is intentionally NOT pre-populated — that path is
  // for "use a different email", and the same email is already
  // surfaced by the "continue as {toEmail}" primary button (Option
  // A). Pre-filling it confused testers per Walter's feedback.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const fn = httpsCallable(functions, 'peekShareInvite');
        const res = await fn({ token });
        if (cancelled) return;
        const info = res?.data || null;
        setInviteInfo(info);
        // If the invite's already expired, surface that immediately
        // rather than making the user sign in just to see the error.
        //
        // Walter 2026-05-11: previously we ALSO errored on accepted=
        // true, but the existing-user share path now writes pre-
        // accepted tokens (so the courtesy email's link lands on the
        // accept-invite page with the Option-A "continue as toEmail"
        // shortcut). The accept Cloud Functions are already idempotent
        // for same-email re-clicks. So we let the user proceed and
        // only block a same-uid mismatch at submit time on the server.
        // Different-user attempts to claim the link still get blocked
        // by acceptShareInvite's check.
        if (info?.expired) {
          setStatus('error');
          setErrorMsg('this invite has expired.');
        }
      } catch (err) {
        if (cancelled) return;
        // peekShareInvite throws not-found for invalid/missing
        // tokens. Anything else is unexpected — log it but still
        // surface the user-facing "couldn't accept" state.
        console.warn('[AcceptInvite] peek failed', err);
        setStatus('error');
        setErrorMsg(err?.message || 'this invite link is invalid.');
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Derived: true while peekShareInvite is still in flight and we
  // haven't fallen into an error state. Drives the "preparing your
  // invite…" indicator in the subheader and gates the "continue
  // as {toEmail}" button (which can't render until we know toEmail).
  const peekLoading = !inviteInfo && status === 'awaiting-signin';

  useEffect(() => {
    if (!user || !token) return;
    if (acceptCalledRef.current) return;
    acceptCalledRef.current = true;
    setStatus('accepting');
    (async () => {
      try {
        const fn = httpsCallable(functions, 'acceptShareInvite');
        const res = await fn({ token });
        const data = res?.data || {};
        if (data?.ok && data?.redirectTo) {
          // Walter 2026-05-11: for LIST invites we don't redirect
          // immediately — we open the BoardPickerModal so the
          // invitee can pick which of their own boards the shared
          // list should live in. setAcceptResult parks the
          // server's response; the post-accept-transition useEffect
          // below decides whether to show the picker or jump
          // straight to redirect.
          setAcceptResult({
            kind: data.kind || inviteInfo?.kind || 'board',
            ownerUid: data.ownerUid || null,
            boardId: data.boardId || null,
            listId: data.listId || null,
            targetTitle: data.targetTitle || inviteInfo?.targetTitle || null,
            redirectTo: data.redirectTo,
          });
        } else {
          setStatus('error');
          setErrorMsg('Could not accept this invite.');
        }
      } catch (err) {
        console.error('[AcceptInvite] failed', err);
        setStatus('error');
        // The Cloud Function throws HttpsError with friendly messages
        // for the common cases (expired / not-found / already-accepted).
        setErrorMsg(err?.message || 'Could not accept this invite.');
      }
    })();
  }, [user, token, navigate, inviteInfo?.kind, inviteInfo?.targetTitle]);

  // Post-accept transition. Once acceptResult is set AND user is
  // available, decide between:
  //   - LIST invite with all source ids present → open the board
  //     picker (status='picking').
  //   - BOARD invite, or list invite missing source ids (legacy /
  //     malformed) → full-page-load to redirectTo, same behaviour as
  //     before the picker existed.
  useEffect(() => {
    if (!acceptResult || !user) return;
    if (placementTransitionRef.current) return;
    placementTransitionRef.current = true;
    const isListInviteWithSources =
      acceptResult.kind === 'list' &&
      !!acceptResult.listId &&
      !!acceptResult.ownerUid &&
      !!acceptResult.boardId;
    if (isListInviteWithSources) {
      setStatus('picking');
      setPickerOpen(true);
      return;
    }
    setStatus('success');
    // We deliberately do a FULL PAGE LOAD (window.location.href)
    // instead of an SPA navigate(...). When the user signs in via
    // Google popup on this page, react-router/Firestore subscriptions
    // are mid-flight; an SPA transition can leave AppInner stuck on
    // its boardsLoading=true gate until a manual refresh. A full
    // reload guarantees a clean boot — fresh auth, fresh
    // subscriptions, fresh route resolution.
    setTimeout(() => {
      window.location.href = acceptResult.redirectTo;
    }, 600);
  }, [acceptResult, user]);

  // Default-board pre-selection in the picker. Walter's brief: "Pre-
  // select the user's most-recently-viewed board". We approximate
  // "recently-viewed" with "most-recently-created" — the last board
  // in the boards array (useBoards orders by createdAt asc) — which
  // is the board the user has been working on most recently in 90% of
  // cases. A localStorage-backed "last active board" would be more
  // accurate but adds plumbing not worth shipping in round 1.
  const defaultPickerBoardId = userBoards.length > 0
    ? userBoards[userBoards.length - 1].id
    : '';

  // Picker submit. `pick` is either { boardId } (existing) or
  // { createWithName } (new board). We always end up at /board/{id}
  // via a full page reload — same rationale as the standard accept
  // path (mid-flight subscriptions need a clean boot).
  const handlePickBoard = async (pick) => {
    if (!acceptResult) throw new Error('no invite context');
    const { ownerUid, boardId: sourceBoardId, listId } = acceptResult;
    if (!ownerUid || !sourceBoardId || !listId) {
      throw new Error('invite is missing source info');
    }
    let targetBoardId = pick.boardId;
    let targetLists = [];
    if (pick.createWithName) {
      const seed = stripTemplateContent(TEMPLATE_BLANK);
      const res = await addBoardFromTemplate(seed, pick.createWithName);
      targetBoardId = res?.boardId;
      // New blank board → no existing tiles, default (80, 60) is safe.
    } else if (targetBoardId && user?.uid) {
      // Existing board → fetch its lists so we can compute a
      // non-overlapping starting position for the placed shared list.
      // One round-trip; the accept-invite page is already a once-per-
      // invite path so the latency is fine.
      try {
        targetLists = await fetchBoardLists(user.uid, targetBoardId);
      } catch (err) {
        console.warn('[AcceptInvite.placement] fetchBoardLists failed; using default position', err);
      }
    }
    if (!targetBoardId) {
      throw new Error('could not resolve a target board');
    }
    // Walter 2026-05-26: position the freshly-placed tile at
    // top-left and sort it FIRST on the destination board, matching
    // the ListPage + BoardPage place-on-board handlers unified the
    // same day. Earlier this path cascaded (x: 80 + offset, y: 60
    // + offset, order: maxOrder + 1) — safe-no-overlap but slotted
    // the tile mid-canvas + last-in-order, contradicting user
    // intent ("I just accepted this share, it should be the first
    // thing I see on my board"). Now (80, 60) + minOrder − 1; z
    // still bumps so the tile sits on top in any overlap.
    const maxZ = targetLists.reduce((m, l) => Math.max(m, l.z || 0), 0);
    const minOrder = targetLists.reduce(
      (m, l) => (l.order != null && l.order < m ? l.order : m),
      Infinity,
    );
    const firstOrder = minOrder === Infinity ? 1 : minOrder - 1;
    await setPlacement(listId, {
      sourceOwnerUid: ownerUid,
      sourceBoardId,
      boardId: targetBoardId,
      // Walter 2026-05-11: cache the inviter's name on the placement
      // doc so the per-tile "shared by" band has a name to display
      // without doing a cross-user account-doc read at render time.
      sourceOwnerName: inviteInfo?.fromName || '',
      // Cached title at place-time. The invite payload's targetTitle
      // is the owner's title-as-of-invite; we use it as a fallback
      // when the live list's title is empty so the invitee doesn't
      // see "(empty)" for a list that DID have a name when shared.
      sourceCachedTitle: acceptResult?.targetTitle || inviteInfo?.targetTitle || '',
      x: 80,
      y: 60,
      w: 280,
      h: 320,
      z: maxZ + 1,
      order: firstOrder,
    });
    setPickerOpen(false);
    setStatus('success');
    setTimeout(() => {
      window.location.href = `/board/${targetBoardId}`;
    }, 400);
  };

  // Walter 2026-05-11: dismissing the picker without placing falls
  // back to the standalone /list/:id view (existing behaviour pre-
  // refinements). The share is already accepted server-side; the user
  // can place the list later via the list's ⋯-menu.
  const handleSkipPick = () => {
    setPickerOpen(false);
    setStatus('success');
    if (acceptResult?.redirectTo) {
      setTimeout(() => {
        window.location.href = acceptResult.redirectTo;
      }, 400);
    }
  };

  const handleMagicSubmit = async (e) => {
    e.preventDefault();
    if (!/\S+@\S+\.\S+/.test(magicEmail.trim())) {
      setErrorMsg('Please enter a valid email');
      return;
    }
    setErrorMsg('');
    setMagicState('sending');
    try {
      await sendMagicLink(
        magicEmail.trim(),
        window.location.origin + window.location.pathname,
      );
      setMagicState('sent');
    } catch (err) {
      console.error('[AcceptInvite] magic-link request failed', err);
      if (err && err.code === 'account-suspended') {
        setErrorMsg('This email was used to delete a listicity account in the last 7 days. Check your inbox for the recovery link, or wait until the 7-day window ends.');
      } else {
        setErrorMsg('Could not send the link. Try Google sign-in instead.');
      }
      setMagicState('idle');
    }
  };

  // Option A — same-email magic-link skip. Trust model: clicking the
  // invite-token link is itself proof of inbox possession. The
  // server uses invite.toEmail (NOT a client-supplied email) to
  // get-or-create a Firebase Auth user, mints a custom token, and
  // runs the ACL update + sharedWithMe pointer write. We sign in
  // with the custom token, then full-reload to the redirectTo.
  //
  // Skipped if peekShareInvite hasn't returned toEmail yet, or if
  // the invite is already accepted/expired (the button just won't
  // be rendered in those cases — see SignInBody).
  const handleSameEmailContinue = async () => {
    if (!token) return;
    setErrorMsg('');
    setStatus('accepting');
    // Mark acceptCalledRef so the user-set useEffect above doesn't
    // ALSO fire acceptShareInvite once signInWithCustomToken lands.
    // The server already accepted in this same call.
    acceptCalledRef.current = true;
    try {
      const fn = httpsCallable(functions, 'acceptShareInviteAtSameEmail');
      const res = await fn({ token });
      const data = res?.data || {};
      if (!data.ok || !data.customToken || !data.redirectTo) {
        throw new Error('server returned an unexpected response');
      }
      await signInWithCustomToken(auth, data.customToken);
      // Walter 2026-05-11: stash the response so the post-accept
      // transition useEffect can pick up from here. For LIST invites
      // this opens the board picker; for board invites it falls
      // through to the full-page redirect. Without this state hop the
      // page used to redirect immediately, skipping the picker
      // entirely on the same-email path.
      setAcceptResult({
        kind: data.kind || inviteInfo?.kind || 'board',
        ownerUid: data.ownerUid || null,
        boardId: data.boardId || null,
        listId: data.listId || null,
        targetTitle: data.targetTitle || inviteInfo?.targetTitle || null,
        redirectTo: data.redirectTo,
      });
    } catch (err) {
      console.error('[AcceptInvite] same-email accept failed', err);
      setStatus('error');
      setErrorMsg(err?.message || 'Could not accept this invite.');
    }
  };

  return (
    <div style={{
      height: '100vh', display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      background: 'var(--bg)',
      padding: '20px',
    }}>
      <div style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: '8px',
        padding: '40px 44px 36px',
        maxWidth: '420px',
        width: '100%',
        textAlign: 'center',
      }}>
        <div style={{
          marginBottom: '24px',
          display: 'flex', justifyContent: 'center',
        }}>
          <Wordmark fontSize={26} scaleWithUi={false} />
        </div>

        <div style={{
          fontFamily: 'var(--mono)',
          fontSize: '15px',
          color: 'var(--text)',
          marginBottom: '8px',
          fontWeight: 600,
        }}>
          you're invited
        </div>
        <div style={{
          fontFamily: 'var(--mono)',
          fontSize: '13px',
          color: 'var(--text-dim)',
          marginBottom: '24px',
          lineHeight: 1.6,
          minHeight: '24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
        }}>
          {peekLoading ? (
            <>
              <Spinner />
              <span>preparing your invite…</span>
            </>
          ) : inviteInfo ? (
            <span>
              <span style={{ color: 'var(--text)' }}>{inviteInfo.fromName || 'Someone'}</span>
              {' invited you to '}
              <span style={{ color: 'var(--text)' }}>"{inviteInfo.targetTitle || 'a board'}"</span>
              {inviteInfo.role === 'editor' ? ' as an editor.' : ' as a viewer.'}
            </span>
          ) : (
            // Kind-neutral fallback — shown before the invite peek resolves,
            // so we don't know yet if it's a board or a list (Walter
            // 2026-07-19: said "board" for a list invite).
            <span>someone shared a board or list with you on listicity.</span>
          )}
        </div>

        {/* State-driven body */}
        {status === 'awaiting-signin' && !user && (
          <SignInBody
            magicState={magicState}
            magicEmail={magicEmail}
            setMagicEmail={(v) => { setMagicEmail(v); setErrorMsg(''); }}
            onGoogle={signInWithGoogle}
            onMagicSubmit={handleMagicSubmit}
            onMagicReset={() => { setMagicState('idle'); setMagicEmail(''); }}
            errorMsg={errorMsg}
            inviteToEmail={inviteInfo?.toEmail || null}
            inviteAlreadyAccepted={!!inviteInfo?.accepted}
            onSameEmailContinue={handleSameEmailContinue}
          />
        )}

        {status === 'accepting' && (
          <StatusBlock
            tone="info"
            title="adding you…"
            body="just a moment while we set up access."
          />
        )}

        {status === 'picking' && (
          <StatusBlock
            tone="info"
            title="✓ access granted"
            body="pick a board to place this list in…"
          />
        )}

        {status === 'success' && (
          <StatusBlock
            tone="ok"
            title="✓ you're in"
            body="taking you to the board…"
          />
        )}

        {status === 'error' && (
          <>
            <StatusBlock
              tone="err"
              title="couldn't accept this invite"
              body={errorMsg || 'this link may have expired or already been used.'}
            />
            <button
              type="button"
              onClick={() => navigate('/', { replace: true })}
              style={{
                marginTop: '16px',
                background: 'transparent',
                border: '1px solid var(--border-bright)',
                color: 'var(--text-dim)',
                fontFamily: 'var(--mono)',
                fontSize: '13px',
                padding: '10px 16px',
                borderRadius: 'var(--radius)',
                cursor: 'pointer',
              }}
            >
              go to listicity
            </button>
          </>
        )}
      </div>

      <BoardPickerModal
        open={pickerOpen}
        onClose={handleSkipPick}
        onPick={handlePickBoard}
        boards={userBoards}
        defaultBoardId={defaultPickerBoardId}
        listTitle={acceptResult?.targetTitle || inviteInfo?.targetTitle}
        ownerName={inviteInfo?.fromName}
        title="where should this list live?"
      />
    </div>
  );
}

// ───────── helpers ─────────

function SignInBody({
  magicState, magicEmail, setMagicEmail,
  onGoogle, onMagicSubmit, onMagicReset, errorMsg,
  // Option A: same-email magic-link skip. When peekShareInvite has
  // returned the toEmail, render a primary "continue as {toEmail}"
  // button that signs the recipient in instantly via a custom token
  // — no second email round-trip. Skipped when toEmail is unknown
  // or when the invite was already accepted (clicking would just
  // surface an error).
  inviteToEmail, inviteAlreadyAccepted, onSameEmailContinue,
}) {
  if (magicState === 'sent') {
    return (
      <div style={{ textAlign: 'left' }}>
        <div style={{
          fontFamily: 'var(--mono)',
          fontSize: '16px',
          color: 'var(--accent)',
          fontWeight: 600,
          marginBottom: '10px',
          textAlign: 'center',
        }}>
          ✓ check your inbox
        </div>
        <div style={{
          fontFamily: 'var(--mono)',
          fontSize: '13px',
          color: 'var(--text-dim)',
          lineHeight: 1.6,
          marginBottom: '16px',
          textAlign: 'center',
        }}>
          we sent a sign-in link to <span style={{ color: 'var(--text)' }}>{magicEmail}</span>.
          click it from this device — you'll come back here and the share will be added.
        </div>
        <div style={{ textAlign: 'center' }}>
          <button
            type="button"
            onClick={onMagicReset}
            style={{
              background: 'none',
              border: '1px solid var(--border-bright)',
              color: 'var(--text-dim)',
              fontFamily: 'var(--mono)',
              fontSize: '13px',
              padding: '8px 14px',
              borderRadius: 'var(--radius)',
              cursor: 'pointer',
            }}
          >
            use a different option
          </button>
        </div>
      </div>
    );
  }

  // Walter 2026-05-11: show the same-email button even for pre-
  // accepted invites (existing-user courtesy path). The Cloud
  // Functions are idempotent for same-email re-claims; clicking the
  // button just signs the recipient in at toEmail and lands them on
  // the resource. Without this, signed-out invitees clicking the
  // courtesy email saw the generic sign-in page with no smart shortcut.
  const showSameEmailButton =
    !!inviteToEmail && !!onSameEmailContinue;

  return (
    <>
      {/* Same-email primary button (Option A). One click signs the
          recipient in at the invite's email and adds them to the
          share — no second email round-trip. Trust model: clicking
          the original invite link is itself proof of inbox
          possession. Recipient can still pick Google or "use a
          different email" below if they prefer. */}
      {showSameEmailButton && (
        <>
          <ProviderButton onClick={onSameEmailContinue} accent>
            continue as {inviteToEmail}
          </ProviderButton>
          <p style={{
            margin: '8px 0 14px',
            fontSize: '12px',
            color: 'var(--text-dimmer)',
            fontFamily: 'var(--mono)',
            textAlign: 'center',
          }}>
            no second email — signs you in instantly
          </p>
          <Divider>or use a different account</Divider>
        </>
      )}

      <ProviderButton onClick={onGoogle} accent={!showSameEmailButton} icon={<GoogleIcon />}>
        Sign in with Google
      </ProviderButton>

      <Divider>or with a different email</Divider>

      <form onSubmit={onMagicSubmit}>
        <input
          type="email"
          value={magicEmail}
          onChange={(e) => setMagicEmail(e.target.value)}
          placeholder="your@email.com"
          autoComplete="email"
          disabled={magicState === 'sending'}
          style={{
            width: '100%', boxSizing: 'border-box',
            padding: '11px 14px',
            background: 'var(--bg)',
            border: '1px solid var(--border-bright)',
            borderRadius: 'var(--radius)',
            color: 'var(--text)',
            fontFamily: 'var(--mono)',
            fontSize: '14px',
            outline: 'none',
            marginBottom: '8px',
          }}
        />
        <ProviderButton
          type="submit"
          disabled={magicState === 'sending'}
        >
          {magicState === 'sending' ? 'sending…' : 'Email me a sign-in link'}
        </ProviderButton>
      </form>

      {errorMsg && (
        <div style={{
          marginTop: '10px',
          color: 'var(--danger)',
          fontFamily: 'var(--mono)',
          fontSize: '12px',
        }}>
          {errorMsg}
        </div>
      )}

      <p style={{
        marginTop: '20px',
        fontSize: '12px',
        color: 'var(--text-dimmer)',
        fontFamily: 'var(--mono)',
        lineHeight: 1.6,
      }}>
        any email works — sign in with the one above, or use a different account. the invite is tied to this link, not the email.
      </p>
    </>
  );
}

function StatusBlock({ tone, title, body }) {
  const color =
    tone === 'ok' ? 'var(--accent)' :
    tone === 'err' ? 'var(--danger, #d04a4a)' :
    'var(--text)';
  return (
    <div>
      <div style={{
        fontFamily: 'var(--mono)',
        fontSize: '16px',
        color,
        fontWeight: 600,
        marginBottom: '8px',
      }}>
        {title}
      </div>
      <div style={{
        fontFamily: 'var(--mono)',
        fontSize: '13px',
        color: 'var(--text-dim)',
        lineHeight: 1.6,
      }}>
        {body}
      </div>
    </div>
  );
}

function Spinner() {
  // Tiny 12px circular spinner — used in the subheader during the
  // peekShareInvite round-trip so the user sees that we're working
  // rather than wondering at a half-rendered card. CSS keyframes
  // are defined globally in index.css ('lists-spin'), but we
  // declare a fallback inline animation in case that class is
  // missing or stripped.
  return (
    <span
      aria-hidden="true"
      style={{
        display: 'inline-block',
        width: '12px',
        height: '12px',
        borderRadius: '50%',
        border: '1.5px solid var(--accent)',
        borderTopColor: 'transparent',
        animation: 'lists-spin 0.8s linear infinite',
      }}
    />
  );
}

function ProviderButton({ children, onClick, type = 'button', accent, icon, disabled }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{
        width: '100%', boxSizing: 'border-box',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        gap: '10px',
        background: accent ? 'var(--accent)' : 'transparent',
        color: accent ? 'var(--bg)' : 'var(--accent)',
        border: accent ? '1px solid var(--accent)' : '1px solid var(--accent-dim)',
        borderRadius: 'var(--radius)',
        fontFamily: 'var(--mono)',
        fontSize: '14px',
        fontWeight: accent ? 600 : 400,
        padding: '12px 16px',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        transition: 'background 0.15s, color 0.15s',
      }}
      onMouseEnter={e => {
        if (disabled) return;
        if (accent) {
          e.currentTarget.style.background = 'var(--accent-dim)';
          e.currentTarget.style.borderColor = 'var(--accent-dim)';
        } else {
          e.currentTarget.style.background = 'var(--accent)';
          e.currentTarget.style.color = 'var(--bg)';
        }
      }}
      onMouseLeave={e => {
        if (disabled) return;
        if (accent) {
          e.currentTarget.style.background = 'var(--accent)';
          e.currentTarget.style.borderColor = 'var(--accent)';
        } else {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.color = 'var(--accent)';
        }
      }}
    >
      {icon}
      {children}
    </button>
  );
}

function Divider({ children }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center',
      gap: '10px',
      margin: '14px 0',
      fontFamily: 'var(--mono)',
      fontSize: '12px',
      color: 'var(--text-dimmer)',
      textTransform: 'uppercase',
      letterSpacing: '1px',
    }}>
      <div style={{ flex: 1, height: '1px', background: 'var(--border)' }} />
      {children}
      <div style={{ flex: 1, height: '1px', background: 'var(--border)' }} />
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}
